import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { GlobalContext } from "./GlobalContext";
import { DropdownMenu } from "@radix-ui/themes";
import useSlidingIndicator from "./hooks/useSlidingIndicator";
import { tabPermission, canSeeTab } from "./tabPermissions";
import {
    LayoutDashboard, Car, HandCoins, Users, Handshake, Store,
    Banknote, BarChart3, Settings, Recycle, MoreHorizontal, MessageSquare, Inbox,
} from "lucide-react";

//Single source of truth for both the desktop strip (in order) and the mobile
//bottom nav (mobileMain: the four slots; the rest live under mobile "More").
//`perm` is attached from tabPermissions.js rather than written here, so the help
//menu's view of which tabs a user can reach cannot drift from this one.
const TABS = [
    { id: "tab-dashboard",  label: "Dashboard",   Icon: LayoutDashboard, mobileMain: true },
    { id: "tab-inventory",  label: "Inventory",   Icon: Car,             mobileMain: true },
    { id: "tab-sales",      label: "Sales",       Icon: HandCoins,       mobileMain: true },
    { id: "tab-customers",  label: "Customers",   Icon: Users,           mobileMain: true },
    { id: "tab-brokers",    label: "Brokers",     Icon: Handshake },
    { id: "tab-sellers",    label: "Sellers",     Icon: Store },
    //Deliberately not mobileMain: the four bottom-nav slots are the daily ones,
    //and messaging is a periodic task. It lives under mobile "More".
    { id: "tab-messages",   label: "Messages",    Icon: MessageSquare },
    //Leads from the public website (migration 047). Not mobileMain: the four
    //bottom-nav slots are the daily ones and this is permission-gated, so most
    //users will never see it at all.
    { id: "tab-enquiries",  label: "Enquiries",   Icon: Inbox },
    { id: "tab-payments",   label: "Payments",    Icon: Banknote },
    { id: "tab-reports",    label: "Reports",     Icon: BarChart3 },
    { id: "tab-settings",   label: "Settings",    Icon: Settings },
    { id: "tab-recyclebin", label: "Recycle Bin", Icon: Recycle },
].map((t) => ({ ...t, perm: tabPermission(t.id) }));

const MAIN_TABS = TABS.filter((t) => t.mobileMain);
const MORE_TABS = TABS.filter((t) => !t.mobileMain);

//Tabs are content-sized now (a 130px box around "Sales" left ragged gaps), so
//the priority+ split can't be arithmetic on a constant — it measures. Until a
//tab has been measured this estimate stands in: 18px icon + 8px gap + 20px
//padding + ~8.3px per character at 15px Geist. Both numbers track .navtab in
//carstyles.css — they were 28px and 7.2 when it was padding 14px at 13px. Only
//the unmeasured first frame and tabs collapsed into More before they were ever
//measured use this, so a stale value degrades quietly rather than breaking.
const estimateWidth = (label) => Math.round(label.length * 8.3) + 46;

function Navtabs({
    activeTab,
    onTabClick,
    disableTabsForTour,
    tourRunning,
 }) {
    const inactiveTabClasses = "navtab";
    const activeTabClasses = "navtab active-nav-tab";
    const { can } = useContext(GlobalContext);
    const containerRef = useRef(null);
    const [width, setWidth] = useState(0);
    //id -> measured px. Labels are static, so one good measurement per tab is
    //enough; it survives the tab being collapsed into More, where it can no
    //longer be measured but its width is exactly what decides whether it fits.
    const widthsRef = useRef(new Map());
    const [measuredAt, setMeasuredAt] = useState(0);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => {
            const w = entry.contentRect.width;
            //Ignore 0: the strip is display:none on mobile and would otherwise
            //collapse every tab into More the moment we return to desktop.
            if (w > 0) setWidth(w);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const allowedTabs = useMemo(
        () => TABS.filter((t) => canSeeTab(t.id, can)),
        [can]
    );

    const measured = (id, label) => widthsRef.current.get(id) ?? estimateWidth(label);

    //Priority+ split: tabs that don't fit collapse into a trailing More menu.
    //Before the first measurement (width 0) render everything — the container
    //clips overflow, so the unmeasured first frame can't show a scrollbar, and
    //it is also the frame that gives every tab its natural width.
    //While a Joyride tour runs, also render everything: tours target #tab-* ids.
    const totalWidth = allowedTabs.reduce((sum, t) => sum + measured(t.id, t.label), 0);
    const fitsAll = tourRunning || width === 0 || totalWidth <= width;

    let visibleCount = allowedTabs.length;
    if (!fitsAll) {
        const budget = width - measured("tab-more", "More");
        let used = 0;
        visibleCount = 0;
        for (const t of allowedTabs) {
            const w = measured(t.id, t.label);
            if (used + w > budget) break;
            used += w;
            visibleCount++;
        }
        //Two tabs minimum: a strip that is only a More button is not a strip.
        visibleCount = Math.max(2, visibleCount);
    }
    const visibleTabs = allowedTabs.slice(0, visibleCount);
    const overflowTabs = allowedTabs.slice(visibleCount);
    const moreIsActive = overflowTabs.some((t) => t.id === activeTab);

    //Record natural widths after every render that could have changed them.
    //Reading offsetWidth here (pre-paint) keeps the split stable within a frame.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let changed = false;
        for (const btn of el.querySelectorAll("[data-navkey]")) {
            const w = btn.offsetWidth;
            if (w > 0 && widthsRef.current.get(btn.dataset.navkey) !== w) {
                widthsRef.current.set(btn.dataset.navkey, w);
                changed = true;
            }
        }
        //A changed width can change the split, so re-run the fit once.
        if (changed) setMeasuredAt((n) => n + 1);
    });

    //Geist loads with font-display: swap; widths taken in the fallback font are
    //wrong by enough to misplace the last tab.
    useLayoutEffect(() => {
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (cancelled) return;
            widthsRef.current.clear();
            setMeasuredAt((n) => n + 1);
        });
        return () => { cancelled = true; };
    }, []);

    const indicator = useSlidingIndicator(
        containerRef,
        moreIsActive ? "tab-more" : activeTab,
        `${visibleTabs.length}:${measuredAt}`
    );

    return (
        <>
        <div className="navtabs-container" ref={containerRef}>
            {visibleTabs.map(({ id, label, Icon }) => (
                <button
                    key={id}
                    className={activeTab === id ? activeTabClasses : inactiveTabClasses}
                    id={id}
                    data-navkey={id}
                    aria-current={activeTab === id ? "page" : undefined}
                    onClick={() => onTabClick(id)}
                    disabled={id === "tab-inventory" ? disableTabsForTour : undefined}
                >
                    <Icon size={18} aria-hidden="true" />
                    <span>{label}</span>
                </button>
            ))}
            {overflowTabs.length > 0 &&
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                        <button
                            className={moreIsActive ? activeTabClasses : inactiveTabClasses}
                            id="tab-more"
                            data-navkey="tab-more"
                            aria-current={moreIsActive ? "page" : undefined}
                        >
                            <MoreHorizontal size={18} aria-hidden="true" />
                            <span>More</span>
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content side="bottom" align="end">
                        {overflowTabs.map((t) => (
                            //No ids here: #tab-* ids stay unique to the strip buttons
                            //(Joyride tours and tests target them).
                            <DropdownMenu.Item
                                key={t.id}
                                onClick={() => onTabClick(t.id)}
                                style={activeTab === t.id ? { fontWeight: 600 } : undefined}
                            >
                                <t.Icon size={16} aria-hidden="true" />
                                {t.label}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Root>
            }
            <span
                className="tabbar-indicator"
                aria-hidden="true"
                style={{
                    transform: `translateX(${indicator.left}px)`,
                    width: indicator.width,
                    opacity: indicator.ready && indicator.width ? 1 : 0,
                }}
            />
        </div>
        <BottomNav activeTab={activeTab} onTabClick={onTabClick} can={can} />
        </>
    );
}

//Mobile bottom navigation (display:none ≥768px; see carstyles.css).
//onTabClick is called with the tab id string — CarsNav runs the same
//unsaved-changes guard + default-submenu logic as a desktop tab click.
function BottomNav({ activeTab, onTabClick, can }) {
    const moreTabs = MORE_TABS.filter((t) => canSeeTab(t.id, can));
    const moreIsActive = moreTabs.some((t) => t.id === activeTab);

    return (
        <nav className="bottom-nav" aria-label="Main navigation">
            {MAIN_TABS.map(({ id, label, Icon }) => (
                <button
                    key={id}
                    className={`bottom-nav-btn${activeTab === id ? " active" : ""}`}
                    onClick={() => onTabClick(id)}
                    aria-current={activeTab === id ? "page" : undefined}
                >
                    <Icon size={20} aria-hidden="true" />
                    <span>{label}</span>
                </button>
            ))}
            <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                    <button className={`bottom-nav-btn${moreIsActive ? " active" : ""}`}>
                        <MoreHorizontal size={20} aria-hidden="true" />
                        <span>More</span>
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content side="top" align="end">
                    {moreTabs.map((t) => (
                        <DropdownMenu.Item key={t.id} onClick={() => onTabClick(t.id)}>
                            {t.label}
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Root>
        </nav>
    );
}

export default Navtabs;
